﻿import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import moment from 'moment';
import { FaCalendarAlt, FaUsers, FaClock, FaExclamationTriangle, FaUserMd, FaChevronLeft, FaChevronRight, FaEye } from 'react-icons/fa';
import Chip from '../../components/Chip';

interface Alert {
  id: number;
  message: string;
  type: 'info' | 'warning' | 'error';
  created_by: string;
  created_at: string;
}

interface ClinicalAlert {
  id: number;
  message: string;
  patient_name?: string;
  priority: 'low' | 'medium' | 'high';
  created_at: string;
}

interface Appointment {
  id: number;
  patient_id: string;  // UUID
  patient_name: string;
  patient_mrn: string;
  patient_dob: string;
  patient_phone: string;
  appointment_type: string;
  start_time: string;
  end_time: string;
  status: 'scheduled' | 'in_waiting_room' | 'in_consult' | 'completed' | 'cancelled' | 'no_show';
  room_name?: string;
  notes?: string;
}

const ClinicianDashboard: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [clinicalAlerts, setClinicalAlerts] = useState<ClinicalAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNextPatientSidebar, setShowNextPatientSidebar] = useState(true);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | null>(null);

  // Calculate statistics
  const todayPatientCount = appointments.filter(
    apt => apt.status !== 'cancelled' && apt.status !== 'no_show'
  ).length;

  const remainingPatients = appointments.filter(
    apt => apt.status === 'scheduled' || apt.status === 'in_waiting_room'
  ).length;

  const inWaitingRoom = appointments.filter(
    apt => apt.status === 'in_waiting_room'
  ).length;

  // Get selected appointment or fall back to current patient in consult
  const selectedAppointment = selectedAppointmentId 
    ? appointments.find(apt => apt.id === selectedAppointmentId)
    : appointments.find(apt => apt.status === 'in_consult');
    
  const currentPatient = selectedAppointment || appointments.find(apt => apt.status === 'in_consult');
  
  const nextPatient = appointments.find(
    apt => apt.status === 'in_waiting_room' || apt.status === 'scheduled'
  );

  // Load appointments
  useEffect(() => {
    loadAppointments();
    loadAlerts();
    loadClinicalAlerts();
  }, [selectedDate, token]);

  const loadAppointments = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const dateStr = moment(selectedDate).format('YYYY-MM-DD');
      const response = await apiCall(
        `/scheduling/appointments/?date=${dateStr}`,
        { method: 'GET' },
        token
      );
      const data = await response.json();
      
      // DEBUG: Log the raw API response
      console.log('=== RAW API RESPONSE ===');
      console.log('Full data:', data);
      console.log('First appointment FULL:', (data.results || data || [])[0]);
      console.log('First appointment patient field:', (data.results || data || [])[0]?.patient);
      console.log('First appointment patient_details field:', (data.results || data || [])[0]?.patient_details);
      
      // Map the API response to our expected format
      const mappedAppointments = (data.results || data || []).map((apt: any) => {
        console.log('Mapping appointment:', {
          id: apt.id,
          patient_raw: apt.patient,
          patient_details: apt.patient_details,
          patient_details_id: apt.patient_details?.id,
          title: apt.title,
          status: apt.status,
          room_name: apt.room_name
        });
        
        // Use patient_details if available
        const patientDetails = apt.patient_details || {};
        
        // patient_id should be the UUID, which is in apt.patient (FK) or apt.patient_details.id
        const patientUUID = apt.patient; // This is the FK which IS the UUID!
        
        console.log('Patient UUID extracted:', patientUUID);
        
        return {
          id: apt.id,
          patient_id: patientUUID,  // Use the FK which is the actual UUID
          patient_name: patientDetails.full_name || 
                       (patientDetails.first_name && patientDetails.last_name 
                         ? `${patientDetails.first_name} ${patientDetails.last_name}` 
                         : apt.patient_name || 'Unknown Patient'),
          patient_mrn: patientDetails.patient_id || '',  // This is the NUMERIC sorting ID for display
          patient_dob: patientDetails.date_of_birth || '',
          patient_phone: patientDetails.phone || patientDetails.mobile_phone || '',
          // apt.title is the calendar event's display title (e.g. "Demo Day
          // Surgery SC" -- facility + type abbreviation), not the
          // appointment type itself. The real type lives on
          // appointment_type_details/procedure_type_name; title is only a
          // last-resort fallback if neither is populated.
          appointment_type: apt.appointment_type_details?.name || apt.procedure_type_name || apt.title || 'Appointment',
          start_time: moment(apt.start_time).format('HH:mm'),
          end_time: moment(apt.end_time).format('HH:mm'),
          status: apt.status === 'checked_in' ? 'in_waiting_room' : apt.status,
          room_name: apt.room_name || apt.location || '',
          notes: apt.notes || apt.description || apt.procedure_description || ''
        };
      });
      
      console.log('=== MAPPED APPOINTMENTS ===');
      console.log(JSON.stringify(mappedAppointments, null, 2));
      
      // Sort appointments chronologically by start time
      mappedAppointments.sort((a: any, b: any) => {
        const timeA = moment(a.start_time, 'HH:mm');
        const timeB = moment(b.start_time, 'HH:mm');
        return timeA.diff(timeB);
      });
      
      setAppointments(mappedAppointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
      // Use mock data for demo - realistic times at 10-minute intervals
      const mockAppointments = [
        {
          id: 1,
          patient_id: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d',
          patient_name: 'Brittany Richardson',
          patient_mrn: 'MRN001234',
          patient_dob: '1985-03-15',
          patient_phone: '0412 345 678',
          appointment_type: 'Follow-up Consultation',
          start_time: '15:40',
          end_time: '16:00',
          status: 'no_show' as const, // Past appointment time, not in waiting room
          room_name: 'Consult Room 1',
          notes: 'Review previous treatment'
        },
        {
          id: 2,
          patient_id: 'b2c3d4e5-f6a7-4b5c-9d0e-1f2a3b4c5d6e',
          patient_name: 'Mark Archer',
          patient_mrn: 'MRN001235',
          patient_dob: '1972-08-22',
          patient_phone: '0423 456 789',
          appointment_type: 'Follow-up Consultation',
          start_time: '15:40',
          end_time: '16:00',
          status: 'no_show' as const, // Past appointment time, not in waiting room
          room_name: 'Consult Room 2'
        },
        {
          id: 3,
          patient_id: 'c3d4e5f6-a7b8-4c5d-0e1f-2a3b4c5d6e7f',
          patient_name: 'Mark Archer',
          patient_mrn: 'MRN001235',
          patient_dob: '1972-08-22',
          patient_phone: '0423 456 789',
          appointment_type: 'Today\'s Appointment',
          start_time: '20:30',
          end_time: '21:00',
          status: 'scheduled' as const,
          room_name: 'Consult Room 1'
        },
        {
          id: 4,
          patient_id: 'd4e5f6a7-b8c9-4d5e-1f2a-3b4c5d6e7f8a',
          patient_name: 'Brittany Richardson',
          patient_mrn: 'MRN001234',
          patient_dob: '1985-03-15',
          patient_phone: '0412 345 678',
          appointment_type: 'Today\'s Appointment',
          start_time: '20:30',
          end_time: '21:00',
          status: 'scheduled' as const,
          room_name: 'Consult Room 2'
        },
        {
          id: 5,
          patient_id: 'e5f6a7b8-c9d0-4e5f-2a3b-4c5d6e7f8a9b',
          patient_name: 'Brittany Richardson',
          patient_mrn: 'MRN001234',
          patient_dob: '1985-03-15',
          patient_phone: '0412 345 678',
          appointment_type: 'Initial Consultation',
          start_time: '21:00',
          end_time: '21:30',
          status: 'scheduled' as const,
          room_name: 'Consult Room 1',
          notes: 'New patient assessment'
        },
        {
          id: 6,
          patient_id: 'f6a7b8c9-d0e1-4f5a-3b4c-5d6e7f8a9b0c',
          patient_name: 'Mark Archer',
          patient_mrn: 'MRN001235',
          patient_dob: '1972-08-22',
          patient_phone: '0423 456 789',
          appointment_type: 'Initial Consultation',
          start_time: '21:00',
          end_time: '21:30',
          status: 'scheduled' as const,
          room_name: 'Consult Room 2'
        },
        {
          id: 7,
          patient_id: 'a7b8c9d0-e1f2-4a5b-4c5d-6e7f8a9b0c1d',
          patient_name: 'Ariana Riddle',
          patient_mrn: 'MRN001236',
          patient_dob: '1958-03-10',
          patient_phone: '0434 567 890',
          appointment_type: 'Follow-up Consultation',
          start_time: '20:00',
          end_time: '20:30',
          status: 'no_show' as const, // Past time, not in waiting room
          room_name: 'Consult Room 1'
        },
        {
          id: 8,
          patient_id: 'b8c9d0e1-f2a3-4b5c-5d6e-7f8a9b0c1d2e',
          patient_name: 'Ariana Riddle',
          patient_mrn: 'MRN001236',
          patient_dob: '1958-03-10',
          patient_phone: '0434 567 890',
          appointment_type: 'Follow-up Consultation',
          start_time: '20:30',
          end_time: '21:00',
          status: 'no_show' as const, // Past time, not in waiting room
          room_name: 'Consult Room 2'
        },
        {
          id: 9,
          patient_id: 'c9d0e1f2-a3b4-4c5d-6e7f-8a9b0c1d2e3f',
          patient_name: 'Michelle Villa',
          patient_mrn: 'MRN001237',
          patient_dob: '1980-11-05',
          patient_phone: '0445 678 901',
          appointment_type: 'Initial Consultation',
          start_time: '21:00',
          end_time: '21:30',
          status: 'in_waiting_room' as const,
          room_name: 'Consult Room 1',
          notes: 'Neck pain assessment'
        },
        {
          id: 10,
          patient_id: 'd0e1f2a3-b4c5-4d5e-7f8a-9b0c1d2e3f4a',
          patient_name: 'Kevin Garcia',
          patient_mrn: 'MRN001238',
          patient_dob: '1963-07-18',
          patient_phone: '0456 789 012',
          appointment_type: 'Follow-up Consultation',
          start_time: '21:30',
          end_time: '22:00',
          status: 'in_waiting_room' as const,
          room_name: 'Consult Room 1'
        },
        {
          id: 11,
          patient_id: 'e1f2a3b4-c5d6-4e5f-8a9b-0c1d2e3f4a5b',
          patient_name: 'Sarah Mitchell',
          patient_mrn: 'MRN001239',
          patient_dob: '1975-06-12',
          patient_phone: '0467 890 123',
          appointment_type: 'Epidural Injection Procedure',
          start_time: '14:30',
          end_time: '15:30',
          status: 'completed' as const,
          room_name: 'Procedure Room 1',
          notes: 'L4-L5 epidural completed successfully'
        },
        {
          id: 12,
          patient_id: 'f2a3b4c5-d6e7-4f5a-9b0c-1d2e3f4a5b6c',
          patient_name: 'James Thompson',
          patient_mrn: 'MRN001240',
          patient_dob: '1968-09-28',
          patient_phone: '0478 901 234',
          appointment_type: 'Facet Joint Injection Procedure',
          start_time: '16:00',
          end_time: '17:00',
          status: 'in_waiting_room' as const,
          room_name: 'Procedure Room 2',
          notes: 'C5-C6 facet joint injection'
        }
      ];
      
      // Sort appointments chronologically
      mockAppointments.sort((a, b) => {
        const timeA = moment(a.start_time, 'HH:mm');
        const timeB = moment(b.start_time, 'HH:mm');
        return timeA.diff(timeB);
      });
      
      setAppointments(mockAppointments);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    // Mock data - replace with API call
    setAlerts([
      {
        id: 1,
        message: 'System maintenance scheduled for tonight at 11 PM',
        type: 'info',
        created_by: 'Admin',
        created_at: moment().subtract(2, 'hours').toISOString()
      },
      {
        id: 2,
        message: 'Reminder: Staff meeting tomorrow at 8:30 AM',
        type: 'warning',
        created_by: 'Practice Manager',
        created_at: moment().subtract(1, 'day').toISOString()
      }
    ]);
  };

  const loadClinicalAlerts = async () => {
    // Mock data - replace with API call
    setClinicalAlerts([
      {
        id: 1,
        message: 'Lab results pending review for multiple patients',
        priority: 'high',
        created_at: moment().subtract(3, 'hours').toISOString()
      },
      {
        id: 2,
        message: 'Prescription renewals needed - 3 patients',
        patient_name: 'Various',
        priority: 'medium',
        created_at: moment().subtract(5, 'hours').toISOString()
      }
    ]);
  };

  const handleDateChange = (days: number) => {
    const newDate = moment(selectedDate).add(days, 'days').toDate();
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const handlePatientClick = (patientId: string) => {
    navigate(`/patients/${patientId}`);
  };

  const handleStartConsult = (appointmentId: number) => {
    // Navigate to Current Consult screen
    navigate(`/consult/${appointmentId}`);
  };

  const getAlertIcon = (type: string) => {
    return <FaExclamationTriangle className={type === 'error' ? 'text-error' : type === 'warning' ? 'text-warning' : 'text-info'} />;
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      low: 'badge-success',
      medium: 'badge-warning',
      high: 'badge-error'
    };
    return `badge ${colors[priority as keyof typeof colors] || 'badge-ghost'}`;
  };

  // Same S/W/IC/TB/C/X/N symbol+colour scheme as the scheduler's status
  // indicator (AppointmentsDashboard.tsx's getStatusIndicator) -- a
  // compact symbol with the full status name as a hover tooltip, same
  // status vocabulary as the Waiting Room board.
  const getStatusIndicator = (statusRaw?: string): { label: string; color: string } => {
    const status = (statusRaw || '').toLowerCase();
    if (status === 'tentative') return { label: 'T', color: '#3b82f6' };
    if (status === 'scheduled' || status === 'confirmed') return { label: 'S', color: '#3b82f6' };
    if (status === 'in_waiting_room' || status === 'checked_in') return { label: 'W', color: '#f59e0b' };
    if (status === 'in_consult') return { label: 'IC', color: '#10b981' };
    if (status === 'to_be_billed') return { label: 'TB', color: '#8b5cf6' };
    if (status === 'completed') return { label: 'C', color: '#6b7280' };
    if (status === 'cancelled') return { label: 'X', color: '#ef4444' };
    if (status === 'no_show') return { label: 'N', color: '#f59e0b' };
    return { label: 'S', color: '#3b82f6' };
  };

  return (
    <div className="flex" style={{ height: 'calc(100vh - 140px)' }}>
      {/* Main Content */}
      <div className={`flex-1 overflow-y-auto transition-all duration-300 ${showNextPatientSidebar ? 'mr-80' : ''}`}>
        <div className="p-6 h-full">
          {/* Header with Date Selector */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
              <FaUserMd className="text-primary" />
              Clinician Dashboard
            </h1>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleDateChange(-1)}
                className="btn btn-outline"
              >
                <FaChevronLeft />
              </button>
              
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="text-primary" />
                <input
                  type="date"
                  value={moment(selectedDate).format('YYYY-MM-DD')}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  className="input input-bordered"
                />
              </div>
              
              <button
                onClick={goToToday}
                className="btn btn-primary"
              >
                Today
              </button>
              
              <button
                onClick={() => handleDateChange(1)}
                className="btn btn-outline"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="stats shadow bg-base-100">
              <div className="stat">
                <div className="stat-figure text-primary">
                  <FaUsers className="text-3xl" />
                </div>
                <div className="stat-title">Today's Patient Count</div>
                <div className="stat-value text-primary">{todayPatientCount}</div>
                <div className="stat-desc">Excluding cancellations & no-shows</div>
              </div>
            </div>

            <div className="stats shadow bg-base-100">
              <div className="stat">
                <div className="stat-figure text-warning">
                  <FaClock className="text-3xl" />
                </div>
                <div className="stat-title">Remaining Patients</div>
                <div className="stat-value text-warning">{remainingPatients}</div>
                <div className="stat-desc">Still to be seen today</div>
              </div>
            </div>

            <div className="stats shadow bg-base-100">
              <div className="stat">
                <div className="stat-figure text-info">
                  <FaUserMd className="text-3xl" />
                </div>
                <div className="stat-title">In Waiting Room</div>
                <div className="stat-value text-info">{inWaitingRoom}</div>
                <div className="stat-desc">Ready to be seen</div>
              </div>
            </div>
          </div>

          {/* Quick View and Appointment List - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Current Patient / Quick View Card */}
            <div className="card bg-gradient-to-r from-primary to-secondary text-primary-content shadow-2xl border-4 border-primary">
              <div className="card-body">
                <h2 className="card-title text-xl flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FaUserMd className="text-2xl" /> 
                    <span>{selectedAppointmentId ? 'QUICK VIEW' : 'CURRENT PATIENT'}</span>
                  </span>
                  {currentPatient && (
                    <div className="badge badge-sm badge-success gap-1 animate-pulse">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-content opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-success-content"></span>
                      </span>
                      {currentPatient.status === 'in_consult' ? 'IN CONSULT' : currentPatient.status.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  )}
                </h2>
                
                {currentPatient ? (
                  <>
                    <div className="space-y-3 mt-2">
                      <div className="text-xl font-bold">{currentPatient.patient_name}</div>
                      <div className="text-sm opacity-90">MRN: {currentPatient.patient_mrn}</div>
                      <div className="text-sm opacity-90">DOB: {moment(currentPatient.patient_dob).format('DD/MM/YYYY')}</div>
                      <div className="text-sm opacity-90">Phone: {currentPatient.patient_phone}</div>
                      <div className="divider my-2"></div>
                      <div className="text-lg font-bold">{currentPatient.appointment_type}</div>
                      <div className="text-sm opacity-90">⏰ Time: {currentPatient.start_time}</div>
                      <div className="text-sm opacity-90">🏥 Room: {currentPatient.room_name}</div>
                      {currentPatient.notes && (
                        <div className="mt-2 p-2 bg-base-100/20 rounded-lg">
                          <p className="font-semibold text-sm">📝 Notes:</p>
                          <p className="opacity-90 text-sm">{currentPatient.notes}</p>
                        </div>
                      )}
                    </div>
                    <div className="card-actions justify-end mt-4 gap-2 flex-wrap">
                      {selectedAppointmentId && (
                        <button
                          onClick={() => setSelectedAppointmentId(null)}
                          className="btn btn-sm btn-outline bg-base-100 text-base-content hover:bg-base-200"
                        >
                          Clear Selection
                        </button>
                      )}
                      <button
                        onClick={() => handlePatientClick(currentPatient.patient_id)}
                        className="btn btn-sm bg-base-100 text-base-content hover:bg-base-200"
                      >
                        <FaEye className="mr-1" />
                        View Full Patient Record
                      </button>
                      {(() => {
                        const isProcedure = currentPatient.appointment_type.toLowerCase().includes('procedure') || 
                                           currentPatient.appointment_type.toLowerCase().includes('injection');
                        
                        if (currentPatient.status === 'completed') {
                          return (
                            <button
                              onClick={() => handleStartConsult(currentPatient.id)}
                              className="btn btn-sm bg-info text-info-content hover:bg-info-focus"
                            >
                              <FaEye className="mr-1" />
                              {isProcedure ? 'Review Procedure' : 'Review Appointment'}
                            </button>
                          );
                        } else if (currentPatient.status === 'in_waiting_room' || currentPatient.status === 'scheduled') {
                          return (
                            <button
                              onClick={() => handleStartConsult(currentPatient.id)}
                              className="btn btn-sm bg-accent text-accent-content hover:bg-accent-focus"
                            >
                              <FaUserMd className="mr-1" />
                              {isProcedure ? 'Start Procedure' : 'Start Appointment'}
                            </button>
                          );
                        } else if (currentPatient.status === 'in_consult') {
                          return (
                            <button
                              onClick={() => handleStartConsult(currentPatient.id)}
                              className="btn btn-sm bg-accent text-accent-content hover:bg-accent-focus"
                            >
                              <FaUserMd className="mr-1" />
                              {isProcedure ? 'Continue Procedure' : 'Continue Appointment'}
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <FaClock className="text-5xl mx-auto mb-3 opacity-60" />
                    <p className="text-lg opacity-80">No patient currently in consultation</p>
                    <p className="text-xs opacity-60 mt-2">
                      {nextPatient 
                        ? `Next patient: ${nextPatient.patient_name} at ${nextPatient.start_time}`
                        : 'No upcoming appointments scheduled'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Appointment List */}
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="card-title text-lg">
                    <FaCalendarAlt className="text-primary" />
                    Appointments List
                  </h2>
                  <div className="badge badge-lg badge-primary">
                    {moment(selectedDate).format('ddd, MMM D')}
                  </div>
                </div>
                
                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <span className="loading loading-spinner loading-lg"></span>
                  </div>
                ) : appointments.length === 0 ? (
                  <div className="text-center py-12 opacity-60">
                    <FaCalendarAlt className="mx-auto text-4xl mb-4" />
                    <p>No appointments scheduled for this date</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table table-xs">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Patient</th>
                          <th>Type</th>
                          <th>Room</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map(apt => {
                          // Determine if this row is selected
                          const isSelected = selectedAppointmentId === apt.id || 
                                           (currentPatient && currentPatient.id === apt.id);
                          
                          // Determine row background color based on appointment type and status
                          let rowClass = 'hover cursor-pointer ';
                          
                          // Highlight selected row
                          if (isSelected) {
                            rowClass += 'border-t-2 border-b-2 border-primary bg-primary/20 ';
                          } else if (apt.status === 'in_consult') {
                            rowClass += 'bg-primary/10 ';
                          } else if (apt.status === 'in_waiting_room') {
                            rowClass += 'bg-info/10 ';
                          } else if (apt.status === 'no_show') {
                            rowClass += 'bg-warning/10 ';
                          } else if (apt.appointment_type.toLowerCase().includes('procedure')) {
                            rowClass += 'bg-warning/5 ';
                          } else if (apt.appointment_type.toLowerCase().includes('initial')) {
                            rowClass += 'bg-secondary/5 ';
                          }
                          
                          return (
                            <tr 
                              key={apt.id} 
                              className={rowClass}
                              onClick={() => setSelectedAppointmentId(apt.id)}
                            >
                              <td className="font-mono font-semibold">{apt.start_time}</td>
                              <td>
                                <div className="font-bold">{apt.patient_name}</div>
                                {apt.patient_mrn && (
                                  <div className="text-xs opacity-60">MRN: {apt.patient_mrn}</div>
                                )}
                              </td>
                              <td>
                                <Chip label={apt.appointment_type} tone="neutral" />
                              </td>
                              <td>{apt.room_name || '-'}</td>
                              <td>
                                <Chip
                                  label={getStatusIndicator(apt.status).label}
                                  color={getStatusIndicator(apt.status).color}
                                  title={`Status: ${apt.status.replace(/_/g, ' ')}`}
                                />
                              </td>
                              <td>
                                <div className="flex gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePatientClick(apt.patient_id);
                                    }}
                                    className="btn btn-xs btn-ghost"
                                    title="View Patient Record"
                                  >
                                    <FaEye />
                                  </button>
                                  {apt.status === 'in_waiting_room' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartConsult(apt.id);
                                      }}
                                      className="btn btn-xs btn-primary"
                                      title={apt.appointment_type.toLowerCase().includes('procedure') || 
                                             apt.appointment_type.toLowerCase().includes('injection')
                                             ? "Start Procedure" 
                                             : "Start Appointment"}
                                    >
                                      Start
                                    </button>
                                  )}
                                  {apt.status === 'completed' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartConsult(apt.id);
                                      }}
                                      className="btn btn-xs btn-info"
                                      title={apt.appointment_type.toLowerCase().includes('procedure') || 
                                             apt.appointment_type.toLowerCase().includes('injection')
                                             ? "Review Procedure" 
                                             : "Review Appointment"}
                                    >
                                      Review
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Patient Sidebar */}
      <div 
        className={`fixed right-0 top-0 h-full w-80 bg-base-100 shadow-2xl transform transition-transform duration-300 ${
          showNextPatientSidebar ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6 h-full overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Next Patient</h2>
            <button
              onClick={() => setShowNextPatientSidebar(false)}
              className="btn btn-sm btn-ghost"
            >
              ✕
            </button>
          </div>

          {nextPatient ? (
            <div className="space-y-4">
              <div className="card bg-info text-info-content">
                <div className="card-body">
                  <h3 className="card-title text-lg">{nextPatient.patient_name}</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-semibold">MRN:</span> {nextPatient.patient_mrn}</p>
                    <p><span className="font-semibold">DOB:</span> {moment(nextPatient.patient_dob).format('DD/MM/YYYY')}</p>
                    <p><span className="font-semibold">Age:</span> {moment().diff(moment(nextPatient.patient_dob), 'years')} years</p>
                    <p><span className="font-semibold">Phone:</span> {nextPatient.patient_phone}</p>
                    <div className="divider my-2"></div>
                    <p><span className="font-semibold">Type:</span> {nextPatient.appointment_type}</p>
                    <p><span className="font-semibold">Time:</span> {nextPatient.start_time} - {nextPatient.end_time}</p>
                    <p><span className="font-semibold">Room:</span> {nextPatient.room_name}</p>
                    {nextPatient.notes && (
                      <>
                        <div className="divider my-2"></div>
                        <p><span className="font-semibold">Notes:</span> {nextPatient.notes}</p>
                      </>
                    )}
                  </div>
                  <div className="card-actions justify-end mt-4">
                    <button
                      onClick={() => handlePatientClick(nextPatient.patient_id)}
                      className="btn btn-sm"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>

              {/* Upcoming Patients */}
              <div>
                <h3 className="font-bold mb-3">Upcoming</h3>
                <div className="space-y-2">
                  {appointments
                    .filter(apt => 
                      apt.id !== nextPatient.id && 
                      (apt.status === 'scheduled' || apt.status === 'in_waiting_room')
                    )
                    .slice(0, 5)
                    .map(apt => (
                      <div 
                        key={apt.id} 
                        className="card bg-base-200 shadow cursor-pointer hover:bg-base-300"
                        onClick={() => handlePatientClick(apt.patient_id)}
                      >
                        <div className="card-body p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-sm">{apt.patient_name}</p>
                              <p className="text-xs opacity-60">{apt.appointment_type}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-mono">{apt.start_time}</p>
                              <Chip
                                label={getStatusIndicator(apt.status).label}
                                color={getStatusIndicator(apt.status).color}
                                title={`Status: ${apt.status.replace(/_/g, ' ')}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-base-content opacity-60 mt-10">
              <FaUsers className="text-5xl mx-auto mb-4 opacity-30" />
              <p>No upcoming patients</p>
            </div>
          )}

          {/* Alerts Section */}
          <div className="mt-8">
            <div className="divider"></div>
            
            {/* Admin Alerts */}
            <div className="mb-6">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <FaExclamationTriangle className="text-warning" />
                Alerts
              </h3>
              <div className="space-y-2">
                {alerts.length === 0 ? (
                  <p className="text-sm text-base-content opacity-60">No alerts</p>
                ) : (
                  alerts.map(alert => (
                    <div key={alert.id} className="alert alert-sm">
                      {getAlertIcon(alert.type)}
                      <div className="flex-1">
                        <p className="text-xs font-semibold">{alert.message}</p>
                        <p className="text-xs opacity-60">
                          By {alert.created_by} • {moment(alert.created_at).fromNow()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Clinical Alerts */}
            <div>
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <FaExclamationTriangle className="text-error" />
                Clinical Alerts
              </h3>
              <div className="space-y-2">
                {clinicalAlerts.length === 0 ? (
                  <p className="text-sm text-base-content opacity-60">No clinical alerts</p>
                ) : (
                  clinicalAlerts.map(alert => (
                    <div key={alert.id} className="alert alert-sm">
                      <FaExclamationTriangle className="text-error" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold">{alert.message}</p>
                          <span className={`badge badge-xs ${getPriorityBadge(alert.priority)}`}>
                            {alert.priority}
                          </span>
                        </div>
                        {alert.patient_name && (
                          <p className="text-xs opacity-60">Patient: {alert.patient_name}</p>
                        )}
                        <p className="text-xs opacity-60">{moment(alert.created_at).fromNow()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toggle Sidebar Button (when hidden) */}
      {!showNextPatientSidebar && (
        <button
          onClick={() => setShowNextPatientSidebar(true)}
          className="fixed right-4 top-20 btn btn-primary btn-circle shadow-xl z-50"
          title="Show Next Patient"
        >
          <FaUsers />
        </button>
      )}
    </div>
  );
};

export default ClinicianDashboard;
